import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import userService, { 
  User, 
  CreateUserDto, 
  UpdateUserDto, 
  UserFilters,
  PaginatedResponse,
  UserSession,
  OAuthAccount,
  LoginHistoryEntry,
  BulkOperationResponse
} from '@/services/userService'

interface UserState {
  users: User[]
  currentUser: User | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: UserFilters

  // Extended data
  userSessions: UserSession[]
  userOAuthAccounts: OAuthAccount[]
  userLoginHistory: LoginHistoryEntry[]
  sessionsLoading: boolean
  oauthLoading: boolean
  loginHistoryLoading: boolean

  // Actions
  setFilters: (filters: UserFilters) => void
  fetchUsers: (filters?: UserFilters) => Promise<void>
  fetchUserById: (id: string) => Promise<void>
  createUser: (data: CreateUserDto) => Promise<User>
  updateUser: (id: string, data: UpdateUserDto) => Promise<User>
  deleteUser: (id: string) => Promise<void>
  
  // Account Lifecycle
  deactivateUser: (id: string) => Promise<User>
  reactivateUser: (id: string) => Promise<User>
  anonymizeUser: (id: string) => Promise<void>
  lockUser: (id: string, hours?: number) => Promise<User>
  unlockUser: (id: string) => Promise<User>

  // Sessions
  fetchUserSessions: (id: string) => Promise<void>
  terminateSession: (userId: string, sessionId: string) => Promise<void>
  terminateAllSessions: (userId: string) => Promise<void>

  // OAuth
  fetchUserOAuthAccounts: (id: string) => Promise<void>

  // Login History
  fetchUserLoginHistory: (id: string, limit?: number, offset?: number) => Promise<void>

  // Bulk Operations
  bulkOperation: (userIds: string[], operation: string, params?: Record<string, any>) => Promise<BulkOperationResponse>

  clearError: () => void
  clearCurrentUser: () => void
  clearExtendedData: () => void
}

const useUserStore = create<UserState>()(
  devtools(
    (set, get) => ({
      users: [],
      currentUser: null,
      loading: false,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      filters: {
        page: 1,
        limit: 10
      },
      userSessions: [],
      userOAuthAccounts: [],
      userLoginHistory: [],
      sessionsLoading: false,
      oauthLoading: false,
      loginHistoryLoading: false,

      setFilters: (filters) => {
        set({ filters: { ...get().filters, ...filters } })
      },

      fetchUsers: async (filters = {}) => {
        try {
          set({ loading: true, error: null })
          const currentFilters = { ...get().filters, ...filters }
          const response: PaginatedResponse<User> = await userService.getAll(currentFilters)
          
          set({
            users: response.data,
            pagination: {
              page: currentFilters.page || 1,
              limit: currentFilters.limit || 10,
              total: response.total,
              pages: Math.ceil(response.total / (currentFilters.limit || 10))
            },
            filters: currentFilters,
            loading: false
          })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error loading users',
            loading: false
          })
        }
      },

      fetchUserById: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const user = await userService.getById(id)
          set({ currentUser: user, loading: false })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error loading user',
            loading: false
          })
        }
      },

      createUser: async (data: CreateUserDto) => {
        try {
          set({ loading: true, error: null })
          const user = await userService.create(data)
          
          set((state) => ({
            users: [user, ...state.users],
            loading: false
          }))

          return user
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error creating user',
            loading: false
          })
          throw error
        }
      },

      updateUser: async (id: string, data: UpdateUserDto) => {
        try {
          set({ loading: true, error: null })
          const user = await userService.update(id, data)
          
          set((state) => ({
            users: state.users.map(u => 
              u.user_id === id ? user : u
            ),
            currentUser: state.currentUser?.user_id === id ? user : state.currentUser,
            loading: false
          }))

          return user
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error updating user',
            loading: false
          })
          throw error
        }
      },

      deleteUser: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await userService.delete(id)
          
          set((state) => ({
            users: state.users.map(u =>
              u.user_id === id ? { ...u, is_active: false } : u
            ),
            currentUser: state.currentUser?.user_id === id
              ? { ...state.currentUser, is_active: false }
              : state.currentUser,
            loading: false
          }))
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error deleting user',
            loading: false
          })
          throw error
        }
      },

      // Account Lifecycle
      deactivateUser: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const user = await userService.deactivateUser(id)
          set((state) => ({
            users: state.users.map(u => u.user_id === id ? user : u),
            currentUser: state.currentUser?.user_id === id ? user : state.currentUser,
            loading: false
          }))
          return user
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error deactivating user', loading: false })
          throw error
        }
      },

      reactivateUser: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const user = await userService.reactivateUser(id)
          set((state) => ({
            users: state.users.map(u => u.user_id === id ? user : u),
            currentUser: state.currentUser?.user_id === id ? user : state.currentUser,
            loading: false
          }))
          return user
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error reactivating user', loading: false })
          throw error
        }
      },

      anonymizeUser: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await userService.anonymizeUser(id)
          set({ loading: false })
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error anonymizing user', loading: false })
          throw error
        }
      },

      lockUser: async (id: string, hours: number = 24) => {
        try {
          set({ loading: true, error: null })
          const user = await userService.lockUser(id, hours)
          set((state) => ({
            users: state.users.map(u => u.user_id === id ? user : u),
            currentUser: state.currentUser?.user_id === id ? user : state.currentUser,
            loading: false
          }))
          return user
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error locking user', loading: false })
          throw error
        }
      },

      unlockUser: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const user = await userService.unlockUser(id)
          set((state) => ({
            users: state.users.map(u => u.user_id === id ? user : u),
            currentUser: state.currentUser?.user_id === id ? user : state.currentUser,
            loading: false
          }))
          return user
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Error unlocking user', loading: false })
          throw error
        }
      },

      // Sessions
      fetchUserSessions: async (id: string) => {
        try {
          set({ sessionsLoading: true })
          const sessions = await userService.getUserSessions(id)
          set({ userSessions: sessions, sessionsLoading: false })
        } catch (error: any) {
          set({ sessionsLoading: false })
          console.error('Error fetching sessions:', error)
        }
      },

      terminateSession: async (userId: string, sessionId: string) => {
        try {
          await userService.terminateSession(userId, sessionId)
          set((state) => ({
            userSessions: state.userSessions.filter(s => s.session_id !== sessionId)
          }))
        } catch (error: any) {
          console.error('Error terminating session:', error)
          throw error
        }
      },

      terminateAllSessions: async (userId: string) => {
        try {
          await userService.terminateAllSessions(userId)
          set({ userSessions: [] })
        } catch (error: any) {
          console.error('Error terminating all sessions:', error)
          throw error
        }
      },

      // OAuth
      fetchUserOAuthAccounts: async (id: string) => {
        try {
          set({ oauthLoading: true })
          const accounts = await userService.getUserOAuthAccounts(id)
          set({ userOAuthAccounts: accounts, oauthLoading: false })
        } catch (error: any) {
          set({ oauthLoading: false })
          console.error('Error fetching OAuth accounts:', error)
        }
      },

      // Login History
      fetchUserLoginHistory: async (id: string, limit: number = 50, offset: number = 0) => {
        try {
          set({ loginHistoryLoading: true })
          const result = await userService.getUserLoginHistory(id, limit, offset)
          set({ userLoginHistory: result.data, loginHistoryLoading: false })
        } catch (error: any) {
          set({ loginHistoryLoading: false })
          console.error('Error fetching login history:', error)
        }
      },

      // Bulk Operations
      bulkOperation: async (userIds: string[], operation: string, params?: Record<string, any>) => {
        try {
          set({ loading: true, error: null })
          const result = await userService.bulkOperation({
            userIds,
            operation: operation as any,
            params
          })
          set({ loading: false })
          return result
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Bulk operation failed', loading: false })
          throw error
        }
      },

      clearError: () => set({ error: null }),
      clearCurrentUser: () => set({ currentUser: null }),
      clearExtendedData: () => set({
        userSessions: [],
        userOAuthAccounts: [],
        userLoginHistory: []
      })
    }),
    { name: 'UserStore' }
  )
)

export default useUserStore
